import { Grid } from "@mui/material";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton, DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Delete, Success } from "@/icons/outlined";

type RecipientEditorProps = {
  placeholder?: string;
  recipientCount?: number;
  isLoading?: boolean;
  onRemove?: () => void;
  onEdit?: () => void;
  shouldHideSubtitle?: boolean;
};

export const RecipientEditor = ({
  placeholder,
  recipientCount = 0,
  isLoading = false,
  onRemove,
  onEdit,
  shouldHideSubtitle = false,
}: RecipientEditorProps) => {
  const { t } = useTranslate();

  const displayText =
    placeholder ||
    t("dashboard", recipientCount > 1 ? "email.recipients.added" : "email.recipient.added", {
      count: recipientCount,
    });

  return (
    <Grid
      item
      xs={12}
      sx={(theme) => ({ borderRadius: 1, padding: 2, backgroundColor: theme.palette.surface.neutral.supershy })}
    >
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Grid container alignItems="center" sx={{ height: "100%", display: "flex" }} spacing={1}>
            <Grid item sx={{ display: "flex", alignItems: "center" }}>
              <DashboardIcon vibe="text-positive-quiet">
                <Success />
              </DashboardIcon>
            </Grid>
            <Grid item>
              <DashboardTypography
                vibe="text-neutral-moderate"
                variant="subtitle1"
                data-test="email-recipient-editor-text"
              >
                {displayText}
              </DashboardTypography>
              {!shouldHideSubtitle && (
                <DashboardTypography
                  variant="body2"
                  sx={(theme) => ({
                    color: theme.palette.text.neutral.quiet,
                  })}
                >
                  {t("dashboard", "emailEditor.recipientCountSubtitle")}
                </DashboardTypography>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={1} alignItems="center">
            {!placeholder && onEdit && (
              <Grid item>
                <DashboardButton vibe="neutral" variant="outlined" onClick={onEdit} isLoading={isLoading}>
                  {t("dashboard", "email.recipients.edit")}
                </DashboardButton>
              </Grid>
            )}
            {onRemove && (
              <Grid item>
                <DashboardIconButton
                  vibe="neutral"
                  variant="outlined"
                  onClick={onRemove}
                  isLoading={isLoading}
                  data-test="email-recipient-editor-delete"
                >
                  <Delete />
                </DashboardIconButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
